import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import cn from 'classnames';

import {
  createClientServiceInstance,
  ClientServiceInstance,
  createUserServiceInstance,
  UserServiceInstance,
  TrackerServiceInstance,
  TrackerService
} from 'services';
import { useClientContext, useUserContext, useTranslationContext } from 'hooks';
import * as interfaces from 'interfaces';

import SubHeader from 'components/SubHeader';
import QuestionsList from 'components/QuestionsList';
import AnswersList from 'components/AnswersList';
import HelpTips from 'popups/HelpTips';
import LoginConfirmation from 'popups/LoginConfirmation';

import classes from './ClientLanding.module.scss';

createClientServiceInstance();
createUserServiceInstance();

var initialTrackingComplete: boolean = false
var userProperties: any = {};

export default function ClientLanding() {
  const { languageCode, clientId } = useParams();
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get("token");
  const answerIdFromUrl = searchParams.get("answerId") || searchParams.get("aId") || searchParams.get("answervId");
  const navigate = useNavigate();

  const { client, setClient, setCategories, toggleLockScreen } = useClientContext();
  const { updateUser, setIsVisitor, setIsSignedIn } = useUserContext();
  const { supportedLanguages, setLanguage } = useTranslationContext();

  const [isMobileVisible, setIsMobileVisible] = useState(true);
  const [showLoginConfirmation, setShowLoginConfirmation] = useState(false);

  const mobileToggleColors = { color: client?.openQuestionsAndAnswersColour, borderColor: client?.openQuestionsAndAnswersColour }

  const getUserData = useCallback(
    (client: interfaces.ClientInterface): void => {
      UserServiceInstance?.getUser()
        .then((receivedUser: interfaces.SessionDataInterface) => {
          const { token, user } = receivedUser;

          UserServiceInstance?.storeUserAndSessionData(user, token);
          TrackerServiceInstance?.setAuthorizationHeader(token);

          updateUser(user);
          setIsVisitor(user.accessLevel === 'visitor');
          setIsSignedIn(['user', 'admin'].includes(user.accessLevel));

          userProperties = {
            ...userProperties,

            event_category: 'Auth',
            userId: user.id + '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phone: user.phone + '',
            zip: user.zip + ''
          };

          (window as any).gtag('config', TrackerService.gaTagId, { user_id: user.id, client_id: client.id });
          (window as any).gtag('config', TrackerService.gaTagId, userProperties);
          (window as any).gtag('set', 'userProperties', userProperties);

          return user;
        })
        .then((user: interfaces.UserInterface) => {
          // if (!user.ipa)
          UserServiceInstance?.getIPA().then(ipaRes => {
            const attributes: any = {
              ...user,
              clientId: client ? client.id : user?.client?.id,
              location: `${ipaRes.country} / ${ipaRes.region} - ${ipaRes.city}`,
              ipa: ipaRes.ip,
            }

            console.log('IPA Information', ipaRes, user, userProperties, attributes);

            UserServiceInstance?.updateCurrentUserSession(attributes);

            TrackerServiceInstance?.trackEvent('setUser', userProperties);
            TrackerServiceInstance?.trackEvent('checkIn', { event_category: 'Auth' });
            TrackerServiceInstance?.trackEvent('screen_view', { event_category: 'PageView', screenName: 'Home' });

            updateUser({ ...user, ipa: attributes.ipa, location: attributes.location, clientId: attributes.clientId })
          });
        });
    },
    [setIsSignedIn, setIsVisitor, updateUser]
  );

  const getClientData = useCallback(
    (clientSelector: string) => {
      if (!initialTrackingComplete) {
        initialTrackingComplete = true;

        const storedClientSelector = ClientServiceInstance?.getStoredClientSelector();

        if (storedClientSelector === clientSelector) {
          const storedClient = ClientServiceInstance?.getStoredClient();

          if (storedClient) {
            const catagories: string[] = storedClient.categories.split(',').map(c => c.trim());

            setCategories(catagories);
            setClient(storedClient);
          }
        } else {
          ClientServiceInstance?.getClient(clientSelector)
            .then(receivedClient => {
              const [suppliedFirstName, lastName] = receivedClient.name.split(' ');

              // Exceptions for Client Names
              const displayName: string = (receivedClient.id == '188') ? 'Bold Team' : suppliedFirstName

              const mappedClient: interfaces.ClientInterface = {
                ...receivedClient,
                firstName: displayName,
                lastName
              };
              const catagories: string[] = receivedClient.categories.split(',').map(c => c.trim());

              setCategories(catagories);
              setClient(mappedClient);
              ClientServiceInstance?.storeClient(mappedClient, clientSelector);

              return mappedClient;
            })
            .then(client => {
              getUserData(client);
            });
        }
      }
    },
    [setCategories, setClient, getUserData]
  );

  const getUserDataByMagicToken = useCallback(
    (clientSelector: string, magicToken: string): void => {
      UserServiceInstance?.getUserByMagicLink(magicToken)
        .then((receivedUser: interfaces.SessionDataInterface) => {
          const { token, user } = receivedUser;
          if (user) {
            navigate(`/${clientId}/en`, { replace: true });
            UserServiceInstance?.storeUserAndSessionData(user, token);
            TrackerServiceInstance?.setAuthorizationHeader(token);
            localStorage.setItem('helpTipsShown', 'true');
            localStorage.setItem(`lockScreenEntered${user.clientId}`, 'true');
            setIsSignedIn(['user', 'admin'].includes(user.accessLevel));
            toggleLockScreen(false);
          }
        })

    }, [clientId, setIsSignedIn, toggleLockScreen, updateUser])

  useEffect(() => {
    if (!clientId) return;

    if (magicToken) {
      getUserDataByMagicToken(clientId, magicToken);
      return;
    }

    getClientData(clientId);

  }, [clientId, getClientData, getUserDataByMagicToken, magicToken]);

  useEffect(() => {
    if (answerIdFromUrl) {
      changeMobilePage(false);
    }
  }, [answerIdFromUrl]);

  useEffect(() => {
    const updateLanguage = (languageCode: string) => {
      setLanguage(languageCode);
      navigate(`/${window.location.pathname.split('/')[1]}/${languageCode}` + window.location.search, { replace: true });
    };

    const recognizedBrowserLanguage = navigator.language.slice(0,2).toLowerCase();

    if (languageCode && supportedLanguages.includes(languageCode))
      setLanguage(languageCode);
    else
      updateLanguage(supportedLanguages.includes(recognizedBrowserLanguage) ? recognizedBrowserLanguage : 'en');

  }, [languageCode]);

  const changeMobilePage = (isQuestions: boolean) => {
    setIsMobileVisible(isQuestions)

    if (isQuestions)
      TrackerServiceInstance?.trackEvent('screen_view', { event_category: 'PageView', screenName: 'Questions List' });
    else
      TrackerServiceInstance?.trackEvent('screen_view', { event_category: 'PageView', screenName: 'Answers List' });
  }

  const clientName = (
    client.id == '211' ? 'The City'       : // BridgePort
    client.id == '209' ? 'City'           : // White Salmon
    client.id == '223' ? 'Bay City'       : // Bay City
    client.id == '235' ? 'Cold Lake'      : // Cold Lake
    client.id == '241' ? 'Fountain Hills' : // Fountain Hills
    `${client?.firstName || 'Candidate'}`
  )
  const headerText = `${clientName}'s Answers`

  return (
    <>
      <SubHeader />
      {/* {showLoginConfirmation && <LoginConfirmation handleClose={() => setShowLoginConfirmation(false)}/>} */}

      <HelpTips onBeforeVideoStep={() => changeMobilePage(false)} />

      <div className={cn(classes.ClientLanding, 'ClientLanding')}>
        <div className={cn(classes.pageWrapper, 'pageWrapper')}>
          <div className={cn(classes.questionsBlock, 'questionsBlock')}>
            <QuestionsList />
          </div>

          <div className={cn(classes.answersBlock, 'answersBlock')}>
            <AnswersList />
          </div>
        </div>

        <div className={cn(classes.mobileWrapper, 'mobileWrapper')}>
          <div className={classes.mobileToggle}>
            <div className={cn(classes.toggleItem, isMobileVisible && classes.active)} style={mobileToggleColors} onClick={() => changeMobilePage(true)}>
              Open Questions
            </div>

            <div className={cn(classes.toggleItem, !isMobileVisible && classes.active)} style={mobileToggleColors} onClick={() => changeMobilePage(false)}>
              {headerText}
            </div>
          </div>

          {isMobileVisible ? <QuestionsList isMobile={true} /> : <AnswersList isMobile={true} />}
        </div>
      </div>
    </>
  );
}
